import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"
import { post } from "@rails/request.js"

// Manages a single file upload with progress tracking
// Attached to each uploading item element
export default class extends Controller {
  static targets = ["thumbnail", "filename", "filesize", "progressBar", "progressText"]
  static values = { url: String, folderId: Number }

  // Called by multi_upload_controller after cloning template
  start(file, tempId) {
    this.tempId = tempId
    this.element.id = `uploading-${tempId}`
    this.filenameTarget.textContent = file.name
    this.filesizeTarget.textContent = this.formatFileSize(file.size)

    // Image thumbnail preview (skip HEIC — browsers can't render it as ObjectURL)
    if (file.type.startsWith('image/') && !file.type.includes('heic') && !file.type.includes('heif')) {
      this.objectUrl = URL.createObjectURL(file)
      const img = document.createElement('img')
      img.src = this.objectUrl
      img.className = 'size-12 object-cover rounded'
      this.thumbnailTarget.querySelector('.file-icon')?.remove()
      this.thumbnailTarget.appendChild(img)
    }

    // Start DirectUpload with this controller as delegate
    this.upload = new DirectUpload(file, '/rails/active_storage/direct_uploads', this)
    this.upload.create((error, blob) => {
      if (error) {
        this.handleError(error)
      } else {
        this.createPendingUpload(blob)
      }
    })
  }

  // DirectUpload delegate method - called when XHR is ready
  directUploadWillStoreFileWithXHR(request) {
    this.xhr = request
    request.upload.addEventListener("progress", (event) => {
      const progress = Math.round((event.loaded / event.total) * 100)
      this.updateProgress(progress)
    })
  }

  updateProgress(percent) {
    this.progressBarTarget.style.width = `${percent}%`
    this.progressTextTarget.textContent = `${percent}%`
  }

  formatFileSize(bytes) {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`
  }

  cancel() {
    if (this.objectUrl) URL.revokeObjectURL(this.objectUrl)
    this.xhr?.abort()
    this.element.remove()
    this.dispatch('cancelled')
  }

  handleError(error) {
    console.error('Upload error:', error)
    this.element.classList.add('upload-error')
    this.element.classList.replace('border-teal-300', 'border-red-400')
    this.element.classList.replace('ring-teal-100', 'ring-red-200')
    this.element.classList.replace('bg-teal-50/50', 'bg-red-50')
    this.progressBarTarget.classList.replace('bg-teal-500', 'bg-red-500')
    this.progressTextTarget.textContent = 'Failed'
    const errorMessage = error.message || error
    this.filenameTarget.insertAdjacentHTML('afterend', `<p class="text-xs text-red-500 mt-1">${errorMessage}</p>`)
    this.dispatch('error', { detail: { message: errorMessage } })
  }

  async createPendingUpload(blob) {
    this.progressTextTarget.textContent = 'Saving...'

    const directUploadData = { asset: blob.signed_id, temp_id: this.tempId }
    if (this.hasFolderIdValue) {
      directUploadData.folder_id = this.folderIdValue
    }

    const response = await post(this.urlValue, {
      body: JSON.stringify({ direct_upload: directUploadData }),
      contentType: 'application/json',
      responseKind: 'turbo-stream'
    })

    // Cleanup object URL - Turbo Stream will replace this element
    if (this.objectUrl) URL.revokeObjectURL(this.objectUrl)

    if (!response.ok) {
      console.error('Error creating pending upload:', response.statusCode)
      this.dispatch('error', { detail: { message: 'Failed to save upload' } })
    }
  }
}
